import type { RateGrid } from "@redotech/admin-sdk/rpc/schema/rate-tables/parse-carrier-invoice-csv";
import {
  AMAZON_MIN_DAILY_VOLUME,
  UNIUNI_MIN_DAILY_VOLUME,
} from "@redotech/redo-fulfillment-carrier-common/carrier-eligibility-requirements";
import {
  PricingStrategy,
  SpreadUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/carrier-quote";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import type { RedoCarrierRateTables } from "@redotech/redo-fulfillment-rate-table-common/global-rate-tables";
import {
  AMAZON_FUEL_SURCHARGE,
  calculateDhlPickupCostPerPackage,
  calculateDiscountQuote,
  calculateFlatDiscountQuote,
  calculateSpreadPercentQuote,
  calculateSpreadQuote,
  type DhlSettings,
  getDhlAnnualPickupCost,
  getRevSharePercentage,
  lookupRateByIndex,
  lookupRateInTable,
  OZ_PER_LB,
  ozToLb,
  REV_SHARE_CARRIERS,
  type RevShareSettings,
  WEEKDAYS_PER_YEAR,
} from "@redotech/redo-fulfillment-rate-table-common/pricing-calculator";
import {
  OutboundWeightByZoneRateTable,
  WeightUnit,
} from "@redotech/redo-fulfillment-rate-table-common/rate-table";
import {
  type CarrierBreakdown,
  type CarrierEligibility,
  MissingMerchantRateMode,
  type MixedModeCell,
  type MixedModeResults,
  type QuoteCell,
  type QuoteResults,
  type RateCalcCarrier,
} from "../types";

export function formatWeightOz(totalOz: number): string {
  if (totalOz < OZ_PER_LB) {
    return `${totalOz} oz`;
  }
  const lbs = Math.floor(totalOz / OZ_PER_LB);
  const oz = Math.round(totalOz % OZ_PER_LB);
  const lbLabel = lbs === 1 ? "lb" : "lbs";
  if (oz === 0) {
    return `${lbs} ${lbLabel}`;
  }
  return `${lbs} ${lbLabel} ${oz} oz`;
}

export function findNeighborSpreadPercentages(
  grid: { merchantRate: number | null; baseRate: number | null }[][],
  row: number,
  col: number,
  discountPct: number,
): number[] {
  const spreadPcts: number[] = [];
  const numRows = grid.length;
  const numCols = grid[0]?.length ?? 0;

  const getSpreadPct = (r: number, c: number): number | null => {
    const cell = grid[r]?.[c];
    if (!cell || cell.merchantRate === null || cell.baseRate === null) {
      return null;
    }
    const quotedRate = calculateDiscountQuote(
      cell.merchantRate,
      discountPct,
      cell.baseRate,
    );
    const spreadDollars = quotedRate - cell.baseRate;
    return cell.baseRate > 0 ? (spreadDollars / cell.baseRate) * 100 : 0;
  };

  for (let r = row - 1; r >= 0; r--) {
    const pct = getSpreadPct(r, col);
    if (pct !== null) {
      spreadPcts.push(pct);
      break;
    }
  }

  for (let r = row + 1; r < numRows; r++) {
    const pct = getSpreadPct(r, col);
    if (pct !== null) {
      spreadPcts.push(pct);
      break;
    }
  }

  for (let c = col - 1; c >= 0; c--) {
    const pct = getSpreadPct(row, c);
    if (pct !== null) {
      spreadPcts.push(pct);
      break;
    }
  }

  for (let c = col + 1; c < numCols; c++) {
    const pct = getSpreadPct(row, c);
    if (pct !== null) {
      spreadPcts.push(pct);
      break;
    }
  }

  return spreadPcts;
}

export function calculateInterpolatedSpread(
  grid: { merchantRate: number | null; baseRate: number | null }[][],
  row: number,
  col: number,
  baseRate: number,
  discountPct: number,
): number {
  const neighborPcts = findNeighborSpreadPercentages(
    grid,
    row,
    col,
    discountPct,
  );
  if (neighborPcts.length === 0) {
    return 0; // No neighbors found, no spread
  }
  const avgPct = neighborPcts.reduce((a, b) => a + b, 0) / neighborPcts.length;
  return (avgPct / 100) * baseRate;
}

export function calculateQuoteResults(
  rateTables: RedoCarrierRateTables | null,
  rateGrid: RateGrid,
  selectedCarriers: RateCalcCarrier[],
  carrierEligibility: CarrierEligibility | null,
  pricingStrategy: PricingStrategy,
  unit: SpreadUnit,
  amount: number,
  dhlSettings: DhlSettings,
  dailyVolume: number,
  revShareSettings: RevShareSettings,
): QuoteResults {
  // Use the first selected carrier's baseline table as the aggregation grain
  // (carrierStats calls this with [carrier], so there's typically one)
  const baselineTable =
    selectedCarriers.length > 0
      ? getBaselineTableForCarrier(
          rateTables,
          selectedCarriers[0]!,
          carrierEligibility,
        )
      : null;

  if (!baselineTable) {
    return {
      cells: [],
      summary: {
        totalOrders: 0,
        estMerchantSavings: 0,
        estRedoDealSize: 0,
        totalSpread: 0,
        ordersWonPct: 0,
        avgOrderPrice: 0,
        avgSpreadPerOrder: 0,
      },
      carrierBreakdown: [],
    };
  }

  const weightBuckets = baselineTable.yAxis.values;
  const weightUnit = baselineTable.yAxis.units;
  const zones = baselineTable.xAxis.values;

  interface AggEntry {
    totalRate: number;
    orderCount: number;
  }
  const agg = new Map<number, Map<number, AggEntry>>();
  for (let wi = 0; wi < weightBuckets.length; wi++) {
    agg.set(wi, new Map());
    for (let zi = 0; zi < zones.length; zi++) {
      agg.get(wi)!.set(zi, { totalRate: 0, orderCount: 0 });
    }
  }

  for (let mwi = 0; mwi < rateGrid.weightBucketsOz.length; mwi++) {
    const merchantWeightOz = rateGrid.weightBucketsOz[mwi]!;
    const weightLb =
      weightUnit === WeightUnit.LB
        ? ozToLb(merchantWeightOz)
        : merchantWeightOz;
    const baselineWeightIdx = weightBuckets.findIndex(
      (b) => weightLb > b.minWeight && weightLb <= b.maxWeight,
    );
    if (baselineWeightIdx < 0) {
      continue;
    }

    for (let mzi = 0; mzi < rateGrid.zones.length; mzi++) {
      const merchantZone = rateGrid.zones[mzi]!;
      const merchantRate = rateGrid.priceGrid[mwi]?.[mzi] ?? null;
      const orderCount = rateGrid.countGrid[mwi]?.[mzi] ?? 0;
      if (merchantRate === null || orderCount === 0) {
        continue;
      }

      const baselineZoneIdx = zones.indexOf(merchantZone);
      if (baselineZoneIdx < 0) {
        continue;
      }

      const cell = agg.get(baselineWeightIdx)!.get(baselineZoneIdx)!;
      cell.totalRate += merchantRate * orderCount;
      cell.orderCount += orderCount;
    }
  }

  const isRevShareCarrier =
    selectedCarriers.length === 1 &&
    REV_SHARE_CARRIERS.includes(
      selectedCarriers[0]! as (typeof REV_SHARE_CARRIERS)[number],
    );

  const dhlPickupCost =
    selectedCarriers.includes(RateTableCarrier.DHL_ECS) &&
    carrierEligibility?.dhl?.eligible
      ? calculateDhlPickupCostPerPackage(
          getDhlAnnualPickupCost(
            carrierEligibility.dhl.nearestDC,
            carrierEligibility.dhl.distanceMiles,
            dhlSettings.needsGL,
            dhlSettings.weeklyPickups,
          ),
          dailyVolume,
          dhlSettings.weeklyPickups,
        )
      : 0;

  const cells: QuoteCell[] = [];
  const carrierWins = new Map<
    RateCalcCarrier,
    { orders: number; savings: number; dealSize: number; spread: number }
  >();
  for (const carrier of selectedCarriers) {
    carrierWins.set(carrier, { orders: 0, savings: 0, dealSize: 0, spread: 0 });
  }

  let totalOrders = 0;
  let totalMerchantCost = 0;
  let totalMerchantSavings = 0;
  let totalDealSize = 0;
  let totalSpread = 0;
  let ordersWon = 0;

  for (const [wi, weightBucket_] of weightBuckets.entries()) {
    for (const [zi, zone] of zones.entries()) {
      const aggCell = agg.get(wi)!.get(zi)!;
      const weightBucket = weightBucket_!;
      const weightOz =
        weightUnit === WeightUnit.LB
          ? weightBucket.maxWeight * OZ_PER_LB
          : weightBucket.maxWeight;

      if (aggCell.orderCount === 0) {
        cells.push({
          weightOz,
          zone: zone!,
          merchantRate: null,
          redoBaseRate: null,
          quotedRate: null,
          savings: null,
          winningCarrier: null,
          orderCount: 0,
        });
        continue;
      }

      const merchantRate = aggCell.totalRate / aggCell.orderCount;
      totalOrders += aggCell.orderCount;
      totalMerchantCost += merchantRate * aggCell.orderCount;

      let carrierRate = lookupRateByIndex(baselineTable, wi, zi);
      if (
        carrierRate !== null &&
        selectedCarriers[0] === RateTableCarrier.DHL_ECS
      ) {
        carrierRate += dhlPickupCost;
        const bucketWeightLb =
          weightUnit === WeightUnit.LB
            ? weightBucket.maxWeight
            : ozToLb(weightBucket.maxWeight);
        carrierRate += bucketWeightLb * 0.11;
      }
      if (
        carrierRate !== null &&
        selectedCarriers[0] === RateTableCarrier.Amazon
      ) {
        carrierRate *= 1 + AMAZON_FUEL_SURCHARGE;
      }

      if (carrierRate === null) {
        cells.push({
          weightOz,
          zone: zone!,
          merchantRate,
          redoBaseRate: null,
          quotedRate: null,
          savings: null,
          winningCarrier: null,
          orderCount: aggCell.orderCount,
        });
        continue;
      }

      let quotedRate: number;
      let spread: number;
      if (isRevShareCarrier) {
        quotedRate = carrierRate;
        const revSharePct = getRevSharePercentage(
          selectedCarriers[0]!,
          weightOz,
          revShareSettings,
        );
        spread = carrierRate * revSharePct;
      } else if (pricingStrategy === PricingStrategy.Discount) {
        quotedRate =
          unit === SpreadUnit.Dollars
            ? calculateFlatDiscountQuote(merchantRate, amount, carrierRate)
            : calculateDiscountQuote(merchantRate, amount, carrierRate);
        spread = quotedRate - carrierRate;
      } else {
        quotedRate =
          unit === SpreadUnit.Percent
            ? calculateSpreadPercentQuote(carrierRate, amount)
            : calculateSpreadQuote(carrierRate, amount);
        spread = quotedRate - carrierRate;
      }

      const savings = merchantRate - quotedRate;
      const won = savings > 0;

      cells.push({
        weightOz,
        zone: zone!,
        merchantRate,
        redoBaseRate: carrierRate,
        quotedRate,
        savings: won ? savings : null,
        winningCarrier: won ? selectedCarriers[0]! : null,
        orderCount: aggCell.orderCount,
      });

      if (won) {
        ordersWon += aggCell.orderCount;
        totalMerchantSavings += savings * aggCell.orderCount;
        totalDealSize += quotedRate * aggCell.orderCount;
        totalSpread += spread * aggCell.orderCount;

        const carrierData = carrierWins.get(selectedCarriers[0]!);
        if (carrierData) {
          carrierData.orders += aggCell.orderCount;
          carrierData.savings += savings * aggCell.orderCount;
          carrierData.dealSize += quotedRate * aggCell.orderCount;
          carrierData.spread += spread * aggCell.orderCount;
        }
      }
    }
  }

  const carrierBreakdown: CarrierBreakdown[] = [];
  for (const [carrier, data] of carrierWins) {
    if (data.orders > 0) {
      carrierBreakdown.push({
        carrier,
        ordersWon: data.orders,
        ordersPct: totalOrders > 0 ? (data.orders / totalOrders) * 100 : 0,
        totalSavings: data.savings,
        totalDealSize: data.dealSize,
        totalSpread: data.spread,
      });
    }
  }

  carrierBreakdown.sort((a, b) => b.ordersWon - a.ordersWon);

  const avgOrderPrice = totalOrders > 0 ? totalMerchantCost / totalOrders : 0;
  const avgSpreadPerOrder = ordersWon > 0 ? totalSpread / ordersWon : 0;

  return {
    cells,
    summary: {
      totalOrders,
      estMerchantSavings: totalMerchantSavings,
      estRedoDealSize: totalDealSize,
      totalSpread,
      ordersWonPct: totalOrders > 0 ? (ordersWon / totalOrders) * 100 : 0,
      avgOrderPrice,
      avgSpreadPerOrder,
    },
    carrierBreakdown,
  };
}

export interface AggregatedRateData {
  weightBuckets: { minWeight: number; maxWeight: number }[];
  weightUnit: WeightUnit;
  zones: string[];
  /** 2D array: data[weightIdx][zoneIdx] = quoted rate as string */
  data: string[][];
  defaultValue: string;
}

export function buildAggregatedRateData(
  rateTables: RedoCarrierRateTables | null,
  rateGrid: RateGrid,
  carrier: RateCalcCarrier,
  carrierEligibility: CarrierEligibility | null,
  pricingStrategy: PricingStrategy,
  unit: SpreadUnit,
  amount: number,
  missingMerchantRateMode: MissingMerchantRateMode,
  defaultSpreadNoMerchantRate: number,
  defaultSpreadUnit: SpreadUnit,
  dhlSettings: DhlSettings,
  dailyVolume: number,
): AggregatedRateData | null {
  const baselineTable = getBaselineTableForCarrier(
    rateTables,
    carrier,
    carrierEligibility,
  );
  if (!baselineTable) {
    return null;
  }

  const weightBuckets = baselineTable.yAxis.values;
  const weightUnit = baselineTable.yAxis.units;
  const zones = baselineTable.xAxis.values;

  const dhlPickupCost =
    carrier === RateTableCarrier.DHL_ECS && carrierEligibility?.dhl?.eligible
      ? calculateDhlPickupCostPerPackage(
          getDhlAnnualPickupCost(
            carrierEligibility.dhl.nearestDC,
            carrierEligibility.dhl.distanceMiles,
            dhlSettings.needsGL,
            dhlSettings.weeklyPickups,
          ),
          dailyVolume,
          dhlSettings.weeklyPickups,
        )
      : 0;

  interface AggEntry {
    totalRate: number;
    orderCount: number;
  }
  const agg = new Map<number, Map<number, AggEntry>>();

  for (let wi = 0; wi < weightBuckets.length; wi++) {
    agg.set(wi, new Map());
    for (let zi = 0; zi < zones.length; zi++) {
      agg.get(wi)!.set(zi, { totalRate: 0, orderCount: 0 });
    }
  }

  for (let mwi = 0; mwi < rateGrid.weightBucketsOz.length; mwi++) {
    const merchantWeightOz = rateGrid.weightBucketsOz[mwi]!;
    const weightLb =
      weightUnit === WeightUnit.LB
        ? ozToLb(merchantWeightOz)
        : merchantWeightOz;

    const baselineWeightIdx = weightBuckets.findIndex(
      (b) => weightLb > b.minWeight && weightLb <= b.maxWeight,
    );
    if (baselineWeightIdx < 0) {
      continue;
    }

    for (let mzi = 0; mzi < rateGrid.zones.length; mzi++) {
      const merchantZone = rateGrid.zones[mzi]!;
      const merchantRate = rateGrid.priceGrid[mwi]?.[mzi] ?? null;
      const orderCount = rateGrid.countGrid[mwi]?.[mzi] ?? 0;

      if (merchantRate === null || orderCount === 0) {
        continue;
      }

      const baselineZoneIdx = zones.indexOf(merchantZone);
      if (baselineZoneIdx < 0) {
        continue;
      }

      const cell = agg.get(baselineWeightIdx)!.get(baselineZoneIdx)!;
      cell.totalRate += merchantRate * orderCount;
      cell.orderCount += orderCount;
    }
  }

  const precomputedGrid: {
    merchantRate: number | null;
    baseRate: number | null;
  }[][] = [];
  for (const [wi, weightBucket] of weightBuckets.entries()) {
    const row: { merchantRate: number | null; baseRate: number | null }[] = [];
    for (let zi = 0; zi < zones.length; zi++) {
      const aggCell = agg.get(wi)!.get(zi)!;
      const merchantRate =
        aggCell.orderCount > 0 ? aggCell.totalRate / aggCell.orderCount : null;

      const baselineRow = baselineTable.data[wi];
      const baseRateStr = baselineRow?.[zi];
      let baseRate = baseRateStr ? parseFloat(baseRateStr) : null;
      if (baseRate !== null && isNaN(baseRate)) {
        baseRate = null;
      }

      if (baseRate !== null && carrier === RateTableCarrier.DHL_ECS) {
        baseRate += dhlPickupCost;
        const bucketWeightLb =
          weightUnit === WeightUnit.LB
            ? weightBucket!.maxWeight
            : ozToLb(weightBucket!.maxWeight);
        baseRate += bucketWeightLb * 0.11;
      }
      if (baseRate !== null && carrier === RateTableCarrier.Amazon) {
        baseRate *= 1 + AMAZON_FUEL_SURCHARGE;
      }

      row.push({ merchantRate, baseRate });
    }
    precomputedGrid.push(row);
  }

  const data: string[][] = [];
  for (let wi = 0; wi < weightBuckets.length; wi++) {
    const row: string[] = [];
    for (let zi = 0; zi < zones.length; zi++) {
      const { merchantRate, baseRate } = precomputedGrid[wi]![zi]!;

      if (baseRate === null) {
        row.push("0");
        continue;
      }

      let quotedRate: number;
      if (merchantRate !== null) {
        if (pricingStrategy === PricingStrategy.Discount) {
          quotedRate =
            unit === SpreadUnit.Dollars
              ? calculateFlatDiscountQuote(merchantRate, amount, baseRate)
              : calculateDiscountQuote(merchantRate, amount, baseRate);
        } else {
          quotedRate =
            unit === SpreadUnit.Percent
              ? calculateSpreadPercentQuote(baseRate, amount)
              : calculateSpreadQuote(baseRate, amount);
        }
      } else if (pricingStrategy === PricingStrategy.Spread) {
        quotedRate =
          unit === SpreadUnit.Percent
            ? calculateSpreadPercentQuote(baseRate, amount)
            : calculateSpreadQuote(baseRate, amount);
      } else if (
        missingMerchantRateMode === MissingMerchantRateMode.Interpolate
      ) {
        const interpolatedSpread = calculateInterpolatedSpread(
          precomputedGrid,
          wi,
          zi,
          baseRate,
          amount,
        );
        quotedRate = baseRate + interpolatedSpread;
      } else if (defaultSpreadUnit === SpreadUnit.Percent) {
        quotedRate = baseRate + (defaultSpreadNoMerchantRate / 100) * baseRate;
      } else {
        quotedRate = baseRate + defaultSpreadNoMerchantRate;
      }

      row.push(quotedRate.toFixed(2));
    }
    data.push(row);
  }

  return { weightBuckets, weightUnit, zones, data, defaultValue: "0" };
}

export function getBaselineTableForCarrier(
  rateTables: RedoCarrierRateTables | null,
  carrier: RateCalcCarrier,
  carrierEligibility: CarrierEligibility | null,
): OutboundWeightByZoneRateTable | null {
  if (!rateTables?.rateTables) {
    return null;
  }

  switch (carrier) {
    case RateTableCarrier.DHL_ECS: {
      const dcId = carrierEligibility?.dhl?.nearestDC;
      if (!dcId) {
        return null;
      }
      const tables = rateTables.rateTables[RateTableCarrier.DHL_ECS];
      return tables?.find((t) => t.distributionCenterId === dcId) ?? null;
    }
    case RateTableCarrier.Amazon: {
      const tables = rateTables.rateTables[RateTableCarrier.Amazon];
      return tables?.[0] ?? null;
    }
    case RateTableCarrier.UPS: {
      const tables = rateTables.rateTables[RateTableCarrier.UPS];
      return tables?.[0] ?? null;
    }
    case RateTableCarrier.USPS: {
      const tables = rateTables.rateTables[RateTableCarrier.UPS_SUREPOST];
      return tables?.[0] ?? null;
    }
    case RateTableCarrier.UNIUNI: {
      const tables = rateTables.rateTables[RateTableCarrier.UNIUNI];
      return tables?.[0] ?? null;
    }
    default:
      return null;
  }
}

export function getUspsMarketRateTableForCalculation(
  rateTables: RedoCarrierRateTables | null,
): OutboundWeightByZoneRateTable | null {
  if (!rateTables?.rateTables) {
    return null;
  }
  const tables = rateTables.rateTables[RateTableCarrier.USPS];
  return tables?.[0] ?? null;
}

export function buildDummyRateGridFromUspsMarketRates(
  rateTables: RedoCarrierRateTables | null,
): RateGrid | null {
  const uspsTable = getUspsMarketRateTableForCalculation(rateTables);
  if (!uspsTable) {
    return null;
  }

  const weightBucketsOz = uspsTable.yAxis.values.map((bucket) =>
    uspsTable.yAxis.units === WeightUnit.LB
      ? bucket.maxWeight * OZ_PER_LB
      : bucket.maxWeight,
  );

  const zones = [...uspsTable.xAxis.values];
  const priceGrid: (number | null)[][] = [];
  const countGrid: number[][] = [];

  for (let wi = 0; wi < uspsTable.yAxis.values.length; wi++) {
    const priceRow: (number | null)[] = [];
    const countRow: number[] = [];
    for (let zi = 0; zi < uspsTable.xAxis.values.length; zi++) {
      const rate = lookupRateByIndex(uspsTable, wi, zi);
      priceRow.push(rate);
      countRow.push(rate !== null ? 1 : 0);
    }
    priceGrid.push(priceRow);
    countGrid.push(countRow);
  }

  return { weightBucketsOz, zones, priceGrid, countGrid };
}

const MIXED_MODE_CARRIERS: RateCalcCarrier[] = [
  RateTableCarrier.UPS,
  RateTableCarrier.USPS,
  RateTableCarrier.Amazon,
  RateTableCarrier.UNIUNI,
];

const revShareCarrierSet = new Set<RateCalcCarrier>(REV_SHARE_CARRIERS);

export function calculateMixedModeResults(
  rateTables: RedoCarrierRateTables | null,
  rateGrid: RateGrid,
  selectedCarriers: RateCalcCarrier[],
  carrierEligibility: CarrierEligibility | null,
  pricingStrategy: PricingStrategy,
  unit: SpreadUnit,
  amount: number,
  revShareSettings: RevShareSettings,
  dailyVolume: number,
): MixedModeResults {
  const eligibleCarriers = MIXED_MODE_CARRIERS.filter((c) => {
    if (!selectedCarriers.includes(c)) {
      return false;
    }
    if (
      c === RateTableCarrier.Amazon &&
      dailyVolume < AMAZON_MIN_DAILY_VOLUME
    ) {
      return false;
    }
    if (
      c === RateTableCarrier.UNIUNI &&
      dailyVolume < UNIUNI_MIN_DAILY_VOLUME
    ) {
      return false;
    }
    return true;
  });

  const carrierBaselineTables = new Map<
    RateCalcCarrier,
    OutboundWeightByZoneRateTable
  >();
  for (const carrier of eligibleCarriers) {
    const table = getBaselineTableForCarrier(
      rateTables,
      carrier,
      carrierEligibility,
    );
    if (table) {
      carrierBaselineTables.set(carrier, table);
    }
  }

  const carrierWins = new Map<
    RateCalcCarrier,
    { orders: number; savings: number; dealSize: number; spread: number }
  >();
  for (const carrier of eligibleCarriers) {
    carrierWins.set(carrier, { orders: 0, savings: 0, dealSize: 0, spread: 0 });
  }

  let totalOrders = 0;
  let totalMerchantCost = 0;
  let totalSavings = 0;
  let totalSpread = 0;
  let totalDealSize = 0;
  let ordersWon = 0;

  const cells: MixedModeCell[][] = [];

  for (let wi = 0; wi < rateGrid.weightBucketsOz.length; wi++) {
    const weightOz = rateGrid.weightBucketsOz[wi]!;
    const row: MixedModeCell[] = [];

    for (let zi = 0; zi < rateGrid.zones.length; zi++) {
      const zone = rateGrid.zones[zi]!;
      const merchantRate = rateGrid.priceGrid[wi]?.[zi] ?? null;
      const orderCount = rateGrid.countGrid[wi]?.[zi] ?? 0;

      if (merchantRate === null || orderCount === 0) {
        row.push({
          weightOz,
          zone,
          merchantRate: null,
          winningCarrier: null,
          carrierRate: null,
          quotedRate: null,
          savings: null,
          spread: null,
          orderCount: 0,
        });
        continue;
      }

      totalOrders += orderCount;
      totalMerchantCost += merchantRate * orderCount;

      let bestQuotedRate = Infinity;
      let bestCarrier: RateCalcCarrier | null = null;
      let bestBaseRate: number | null = null;
      let bestSpread: number | null = null;

      for (const carrier of eligibleCarriers) {
        const table = carrierBaselineTables.get(carrier);
        if (!table) {
          continue;
        }
        let baseRate = lookupRateInTable(table, weightOz, zone);
        if (baseRate === null) {
          continue;
        }

        if (carrier === RateTableCarrier.Amazon) {
          baseRate *= 1 + AMAZON_FUEL_SURCHARGE;
        }

        let quotedRate: number;
        let spread: number;

        if (revShareCarrierSet.has(carrier)) {
          quotedRate = baseRate;
          const revSharePct = getRevSharePercentage(
            carrier,
            weightOz,
            revShareSettings,
          );
          spread = baseRate * revSharePct;
        } else {
          if (pricingStrategy === PricingStrategy.Discount) {
            quotedRate =
              unit === SpreadUnit.Dollars
                ? calculateFlatDiscountQuote(merchantRate, amount, baseRate)
                : calculateDiscountQuote(merchantRate, amount, baseRate);
          } else {
            quotedRate =
              unit === SpreadUnit.Percent
                ? calculateSpreadPercentQuote(baseRate, amount)
                : calculateSpreadQuote(baseRate, amount);
          }
          spread = quotedRate - baseRate;
        }

        if (quotedRate < bestQuotedRate) {
          bestQuotedRate = quotedRate;
          bestCarrier = carrier;
          bestBaseRate = baseRate;
          bestSpread = spread;
        }
      }

      if (bestCarrier === null || bestBaseRate === null) {
        row.push({
          weightOz,
          zone,
          merchantRate,
          winningCarrier: null,
          carrierRate: null,
          quotedRate: null,
          savings: null,
          spread: null,
          orderCount,
        });
        continue;
      }

      const savings = merchantRate - bestQuotedRate;
      const won = savings > 0;

      row.push({
        weightOz,
        zone,
        merchantRate,
        winningCarrier: bestCarrier,
        carrierRate: bestBaseRate,
        quotedRate: bestQuotedRate,
        savings,
        spread: bestSpread,
        orderCount,
      });

      if (won) {
        ordersWon += orderCount;
        totalSavings += savings * orderCount;
        totalDealSize += bestQuotedRate * orderCount;
        totalSpread += (bestSpread ?? 0) * orderCount;

        const carrierData = carrierWins.get(bestCarrier);
        if (carrierData) {
          carrierData.orders += orderCount;
          carrierData.savings += savings * orderCount;
          carrierData.dealSize += bestQuotedRate * orderCount;
          carrierData.spread += (bestSpread ?? 0) * orderCount;
        }
      }
    }
    cells.push(row);
  }

  const carrierBreakdown: CarrierBreakdown[] = [];
  for (const [carrier, data] of carrierWins) {
    if (data.orders > 0) {
      carrierBreakdown.push({
        carrier,
        ordersWon: data.orders,
        ordersPct: totalOrders > 0 ? (data.orders / totalOrders) * 100 : 0,
        totalSavings: data.savings,
        totalDealSize: data.dealSize,
        totalSpread: data.spread,
      });
    }
  }
  carrierBreakdown.sort((a, b) => b.ordersWon - a.ordersWon);

  const annualOrders = dailyVolume * WEEKDAYS_PER_YEAR;
  const scaleFactor = totalOrders > 0 ? annualOrders / totalOrders : 0;

  return {
    cells,
    carrierBreakdown,
    summary: {
      totalOrders,
      ordersWon,
      ordersWonPct: totalOrders > 0 ? (ordersWon / totalOrders) * 100 : 0,
      totalSavings,
      totalSpread,
      totalDealSize,
      annualOrders,
      annualSavings: totalSavings * scaleFactor,
      annualSpread: totalSpread * scaleFactor,
      annualDealSize: totalDealSize * scaleFactor,
      avgOrderPrice: totalOrders > 0 ? totalMerchantCost / totalOrders : 0,
      avgSpreadPerOrder: ordersWon > 0 ? totalSpread / ordersWon : 0,
    },
  };
}
